import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardStyle } from '@capacitor/keyboard';
import { TabBar } from './components/TabBar';
import { SessionExpiredBanner } from './components/SessionExpiredBanner';
import { useAdminState } from './utils/auth';
import { HomeScreen } from './screens/HomeScreen';
import { CarteScreen } from './screens/CarteScreen';
import { ReserverScreen } from './screens/ReserverScreen';
import { DiscuterScreen } from './screens/DiscuterScreen';
import { CompteScreen } from './screens/CompteScreen';
import { NotificationsScreen } from './screens/NotificationsScreen';
import { CarteCadeauScreen } from './screens/CarteCadeauScreen';
import { GIFT_CARDS_ENABLED, SHOW_PASS_VIEW } from './config';
import { CaveScreen } from './screens/CaveScreen';
import { BottleDetailScreen } from './screens/BottleDetailScreen';
import { ScanScreen } from './screens/ScanScreen';
import { AgendaScreen } from './screens/AgendaScreen';
import { EventPage } from './screens/EventPage';
import { AddressPage } from './screens/AddressPage';
import { AnnulerScreen } from './screens/AnnulerScreen';
import { StaffDashboard } from './screens/staff/StaffDashboard';
import { StaffSalle } from './screens/staff/StaffSalle';
import { StaffCarte } from './screens/staff/StaffCarte';
import { StaffAgenda } from './screens/staff/StaffAgenda';
import { StaffPayment } from './screens/staff/StaffPayment';
import { StaffResa } from './screens/staff/StaffResa';
import { StaffResaDuJour } from './screens/staff/StaffResaDuJour';
import { StaffCarteNavigation } from './screens/staff/StaffCarteNavigation';
import { StaffCarteHebdoScreen } from './screens/staff/StaffCarteHebdoScreen';
import { StaffCompta } from './screens/staff/StaffCompta';
import { StaffMessages } from './screens/staff/StaffMessages';
import { StaffCamera } from './screens/staff/StaffCamera';
import './styles/global.css';

// `/carte-cadeau` n'est fullscreen que si la feature est active — sinon la route
// redirige vers l'accueil (cf. Routes) et ne s'affiche jamais.
const FULLSCREEN_ROUTES = [
  ...(GIFT_CARDS_ENABLED ? ['/carte-cadeau'] : []),
  // Aperçu staff (`/staff/carte-cadeau`) : toujours fullscreen, il rend le même
  // écran que la version client.
  '/staff/carte-cadeau',
  '/reserver', '/discuter', '/lieu', '/staff/payment', '/staff/resa-du-jour',
  '/compte/notifications',
];

/**
 * Garde des routes admin (`/staff/*`). Le masquage d'un onglet ou d'une tuile ne
 * protège rien : sans cette garde, un membre du staff atteint le dashboard, la
 * compta ou l'encaissement en tapant l'URL. Le backend refuse déjà ces appels
 * (requireAdmin → 403), mais l'écran s'ouvrirait quand même, vide et anxiogène.
 *
 * Redirection vers `/reserver` — la première destination visible d'un staff —
 * plutôt que vers `/`, qui le renverrait aussitôt ici par la redirection
 * ci-dessous.
 */
function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { admin } = useAdminState();
  return admin ? children : <Navigate to="/reserver" replace />;
}

function AppContent() {
  const { pathname } = useLocation();
  const isFullscreen = FULLSCREEN_ROUTES.some((r) => pathname.startsWith(r));
  const { admin, authed } = useAdminState();

  // Force le clavier iOS (et sa barre de suggestions QuickType) en mode clair,
  // sinon en dark mode système la barre au-dessus du clavier apparaît sombre et
  // tranche avec le fond clair de l'app (IMG_6291). Renforce le `style: LIGHT`
  // de capacitor.config. No-op hors iOS natif.
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;
    Keyboard.setStyle({ style: KeyboardStyle.Light }).catch(() => {});
  }, []);

  // Defensive fallback to @capacitor/keyboard Body resize : when an input or
  // textarea gains focus, scroll it into the centre of the viewport. The
  // 300 ms delay matches the iOS keyboard animation, so we wait for body
  // resize to settle before measuring. No-op on web (focus -> scrollIntoView
  // is harmless when the keyboard already moves with the viewport).
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag !== 'TEXTAREA' && tag !== 'INPUT') return;
      // Écrans qui gèrent eux-mêmes le scroll clavier (data-kb-scroll="self") :
      // deux scrollIntoView smooth concurrents s'annulent, et `center` repose le
      // champ au milieu d'un viewport qui, hors resize natif, est à moitié caché
      // par le clavier. Cf. ReserverScreen (bug champ Nom masqué).
      if (target.dataset.kbScroll === 'self') return;
      setTimeout(
        () => target.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        300,
      );
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  return (
    <>
      <SessionExpiredBanner />
      <div className={isFullscreen ? 'fullscreen-content' : 'app-content'}>
        <Routes>
          {/* Client */}
          {/* Beta staff-first : un compte connecté non-admin n'a plus d'onglet
              Accueil ; le laisser atterrir sur l'accueil client lui donnerait un
              écran sans onglet actif. On ne redirige QUE s'il est connecté — un
              visiteur anonyme garde l'accueil public de la PWA. */}
          <Route
            path="/"
            element={authed && !admin ? <Navigate to="/reserver" replace /> : <HomeScreen />}
          />
          <Route path="/la-carte" element={<CarteScreen />} />
          {/* Pas de route catch-all : sans cette redirection, /carte ne matche
              rien et n'affiche que la TabBar (page blanche). `replace` évite que
              le bouton retour reboucle sur /carte. */}
          <Route path="/carte" element={<Navigate to="/la-carte" replace />} />
          <Route path="/reserver" element={<ReserverScreen />} />
          <Route path="/discuter" element={<DiscuterScreen />} />
          <Route path="/compte" element={<CompteScreen />} />
          <Route path="/compte/notifications" element={<NotificationsScreen />} />
          {/* Carte cadeau OFF par défaut : on garde la route mais on redirige vers
              l'accueil (plutôt qu'un 404) car des liens PWA /carte-cadeau existent. */}
          <Route
            path="/carte-cadeau"
            element={GIFT_CARDS_ENABLED ? <CarteCadeauScreen /> : <Navigate to="/" replace />}
          />
          <Route path="/cave" element={<CaveScreen />} />
          <Route path="/cave/:id" element={<BottleDetailScreen />} />
          <Route path="/scan" element={<ScanScreen />} />
          <Route path="/agenda" element={<AgendaScreen />} />
          {/* Page événement publique — destination des liens partagés. Ni auth,
              ni redirection staff : un visiteur qui arrive de WhatsApp doit voir
              l'événement, pas un écran de connexion. La TabBar reste affichée,
              c'est sa seule porte d'entrée vers le reste de l'app. */}
          <Route path="/evenement/:id" element={<EventPage />} />
          <Route path="/lieu" element={<AddressPage />} />
          <Route path="/annuler" element={<AnnulerScreen />} />
          {/* Staff */}
          <Route path="/staff/dashboard" element={<RequireAdmin><StaffDashboard /></RequireAdmin>} />
          <Route path="/staff/salle" element={<RequireAdmin><StaffSalle /></RequireAdmin>} />
          <Route path="/staff/carte" element={<RequireAdmin><StaffCarte /></RequireAdmin>} />
          <Route path="/staff/carte/navigation" element={<RequireAdmin><StaffCarteNavigation /></RequireAdmin>} />
          <Route path="/staff/carte-hebdo" element={<RequireAdmin><StaffCarteHebdoScreen /></RequireAdmin>} />
          <Route path="/staff/agenda" element={<RequireAdmin><StaffAgenda /></RequireAdmin>} />
          <Route path="/staff/compta" element={<RequireAdmin><StaffCompta /></RequireAdmin>} />
          <Route path="/staff/payment" element={<RequireAdmin><StaffPayment /></RequireAdmin>} />
          <Route path="/staff/resa" element={<RequireAdmin><StaffResa /></RequireAdmin>} />
          <Route path="/staff/messages" element={<RequireAdmin><StaffMessages /></RequireAdmin>} />
          {/* Vue pass masquée pendant la beta staff (SHOW_PASS_VIEW). Le composant
              reste importé et compilé : seul l'accès est coupé, y compris par URL
              directe — l'iPad mural garde son raccourci en favori. */}
          <Route
            path="/staff/resa-du-jour"
            element={
              <RequireAdmin>
                {SHOW_PASS_VIEW ? <StaffResaDuJour /> : <Navigate to="/staff/resa" replace />}
              </RequireAdmin>
            }
          />
          <Route path="/staff/camera" element={<RequireAdmin><StaffCamera /></RequireAdmin>} />
          {/* Aperçu design de la carte cadeau, réservé aux admins. La vente reste
              OFF (VITE_ENABLE_GIFT_CARDS inchangé) : cette route rend le même
              écran en mode `preview` — parcours et rendu identiques, mais sans
              étape de paiement (aucune session Adyen créée) et sans lien de
              partage. Le chemin client `/carte-cadeau` n'est pas touché. */}
          <Route path="/staff/carte-cadeau" element={<RequireAdmin><CarteCadeauScreen preview /></RequireAdmin>} />
        </Routes>
      </div>
      {!isFullscreen && <TabBar />}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
